import React, { useState } from "react";
import { Play, Pause, SkipBack, SkipForward, ChevronDown, ChevronUp, Repeat, Volume2, VolumeX, Hand, Moon } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useContentI18n } from "@/context/ContentI18n";
import { usePlayer } from "@/context/PlayerContext";
import { ShareButton } from "@/components/ShareButton";
import { fmtTime } from "@/lib/format";

export const PlayerBar = () => {
  const { t, lang, fontClass } = useLang();
  const { title: ctitle } = useContentI18n();
  const {
    current, isPlaying, progress, duration, volume, autoAdvance, needsInteraction,
    expanded, setExpanded, togglePlay, next, prev, seek, changeVolume, toggleAuto, attemptPlay,
    sleepMode, sleepRemaining, setSleepTimer,
  } = usePlayer();
  const [sleepOpen, setSleepOpen] = useState(false);

  if (!current) return null;
  const title = ctitle(current);
  const subtitle = lang === "en" ? current.title_si : current.title_en;
  const dur = duration || current.duration_sec || 0;

  const BigPlay = ({ size = 96 }) => (
    <button
      onClick={togglePlay}
      data-testid="player-play-pause-button"
      aria-label={isPlaying ? t("pause") : t("play")}
      className="grid place-items-center rounded-full transition-transform active:scale-90 hover:scale-105 sk-glow"
      style={{ width: size, height: size, background: "var(--player-accent)", color: "var(--player-bg)" }}
    >
      {isPlaying ? <Pause size={size * 0.42} fill="currentColor" /> : <Play size={size * 0.42} fill="currentColor" style={{ marginLeft: size * 0.04 }} />}
    </button>
  );

  const Scrub = () => (
    <div className="w-full">
      <input
        type="range"
        className="sk-range"
        data-testid="player-scrub-bar"
        min={0}
        max={dur || 0}
        step={0.5}
        value={Math.min(progress, dur || 0)}
        onChange={(e) => seek(parseFloat(e.target.value))}
        aria-label="Seek within song"
        style={{ background: `linear-gradient(to right, var(--player-accent) ${(dur ? (progress / dur) * 100 : 0)}%, rgba(255,255,255,0.18) ${(dur ? (progress / dur) * 100 : 0)}%)` }}
      />
      <div className="flex justify-between font-ui text-sm font-semibold mt-1" style={{ color: "var(--player-text)" }}>
        <span>{fmtTime(progress)}</span>
        <span>{fmtTime(dur)}</span>
      </div>
    </div>
  );

  return (
    <>
      {/* Collapsed bottom bar */}
      <div
        className="fixed bottom-0 inset-x-0 z-40"
        style={{ background: "var(--player-bg)", borderTop: "1px solid rgba(255,255,255,0.08)" }}
        data-testid="player-bar"
      >
        {needsInteraction && (
          <button
            onClick={attemptPlay}
            data-testid="player-tap-continue"
            className="w-full flex items-center justify-center gap-2 py-2 font-ui font-bold text-sm"
            style={{ background: "var(--accent)", color: "#fff" }}
          >
            <Hand size={18} /> {t("tap_continue")}
          </button>
        )}
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 flex items-center gap-3 sm:gap-4">
          <button
            className="flex items-center gap-3 min-w-0 flex-1 text-left"
            onClick={() => setExpanded(true)}
            data-testid="player-expand-button"
            aria-label="Open full player"
          >
            <img src={current.cover_url} alt={current.title_en} className="w-14 h-14 rounded-xl object-cover shrink-0" />
            <span className="min-w-0">
              <span className={`block ${fontClass()} text-base sm:text-lg font-bold truncate`} style={{ color: "var(--player-text)" }}>{title}</span>
              <span className="block font-ui text-sm truncate" style={{ color: "rgba(255,255,255,0.6)" }}>{current.artist}</span>
            </span>
            <ChevronUp size={20} className="shrink-0" style={{ color: "rgba(255,255,255,0.5)" }} />
          </button>

          <button onClick={prev} data-testid="player-prev-button" aria-label={t("prev")} className="hidden sm:grid place-items-center w-12 h-12 rounded-full" style={{ color: "var(--player-text)" }}>
            <SkipBack size={24} fill="currentColor" />
          </button>
          <BigPlay size={56} />
          <button onClick={next} data-testid="player-next-button" aria-label={t("next")} className="grid place-items-center w-12 h-12 rounded-full" style={{ color: "var(--player-text)" }}>
            <SkipForward size={24} fill="currentColor" />
          </button>
        </div>
      </div>

      {/* Expanded full-screen player */}
      {expanded && (
        <div className="fixed inset-0 z-50 flex flex-col sk-scale-in" style={{ background: "var(--player-bg)" }} data-testid="player-fullscreen">
          <div className="flex items-center justify-between px-5 py-4">
            <button onClick={() => setExpanded(false)} data-testid="player-collapse-button" aria-label="Close full player" className="grid place-items-center w-12 h-12 rounded-full" style={{ color: "var(--player-text)", background: "rgba(255,255,255,0.08)" }}>
              <ChevronDown size={26} />
            </button>
            <span className="font-ui text-sm font-bold tracking-widest uppercase" style={{ color: "var(--player-accent)" }}>{t("now_playing")}</span>
            <span className="w-12" />
          </div>

          <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6 overflow-y-auto no-scrollbar">
            <img src={current.cover_url} alt={current.title_en} className="w-64 h-64 sm:w-80 sm:h-80 rounded-3xl object-cover shadow-2xl" style={{ border: "1px solid rgba(255,255,255,0.12)" }} />
            <div className="text-center max-w-lg">
              <h2 className={`${fontClass()} text-3xl sm:text-4xl font-bold leading-tight`} style={{ color: "var(--player-text)" }}>{title}</h2>
              <p className={`${fontClass(lang === "en" ? "si" : "en")} text-lg mt-1`} style={{ color: "rgba(255,255,255,0.65)" }}>{subtitle}</p>
              <p className="font-ui text-lg font-medium mt-2" style={{ color: "var(--player-accent)" }}>{current.artist}</p>
            </div>

            <div className="w-full max-w-xl"><Scrub /></div>

            <div className="flex items-center justify-center gap-6 sm:gap-10">
              <button onClick={prev} data-testid="player-prev-button-full" aria-label={t("prev")} className="grid place-items-center w-16 h-16 rounded-full transition-transform active:scale-90" style={{ color: "var(--player-text)", background: "rgba(255,255,255,0.08)" }}>
                <SkipBack size={30} fill="currentColor" />
              </button>
              <BigPlay size={104} />
              <button onClick={next} data-testid="player-next-button-full" aria-label={t("next")} className="grid place-items-center w-16 h-16 rounded-full transition-transform active:scale-90" style={{ color: "var(--player-text)", background: "rgba(255,255,255,0.08)" }}>
                <SkipForward size={30} fill="currentColor" />
              </button>
            </div>

            <div className="w-full max-w-xl flex flex-col gap-4 pb-8">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <button
                  onClick={toggleAuto}
                  data-testid="player-auto-advance-toggle"
                  aria-pressed={autoAdvance}
                  className="flex items-center gap-2 px-4 h-12 rounded-full font-ui font-bold text-sm w-full sm:w-auto justify-center"
                  style={{ background: autoAdvance ? "var(--player-accent)" : "rgba(255,255,255,0.08)", color: autoAdvance ? "var(--player-bg)" : "var(--player-text)" }}
                >
                  <Repeat size={18} /> {t("auto_next")} {autoAdvance ? "· ✓" : ""}
                </button>

                <button
                  onClick={() => setSleepOpen((o) => !o)}
                  data-testid="player-sleep-toggle"
                  aria-expanded={sleepOpen}
                  className="flex items-center gap-2 px-4 h-12 rounded-full font-ui font-bold text-sm w-full sm:w-auto justify-center"
                  style={{ background: sleepMode ? "var(--player-accent)" : "rgba(255,255,255,0.08)", color: sleepMode ? "var(--player-bg)" : "var(--player-text)" }}
                >
                  <Moon size={18} />
                  {sleepMode ? (
                    <span>{t("sleep_on")} {sleepMode === "end" ? t("sleep_end") : `${Math.ceil(sleepRemaining / 60)} ${t("sleep_min")}`}</span>
                  ) : (
                    <span>{t("sleep")}</span>
                  )}
                </button>
              </div>

              {sleepOpen && (
                <div className="flex flex-wrap gap-2 justify-center sk-scale-in" data-testid="player-sleep-options">
                  {[
                    { v: null, label: t("sleep_off") },
                    { v: 15, label: `15 ${t("sleep_min")}` },
                    { v: 30, label: `30 ${t("sleep_min")}` },
                    { v: 45, label: `45 ${t("sleep_min")}` },
                    { v: 60, label: `60 ${t("sleep_min")}` },
                    { v: "end", label: t("sleep_end") },
                  ].map((opt) => {
                    const active = sleepMode === opt.v;
                    return (
                      <button
                        key={String(opt.v)}
                        onClick={() => { setSleepTimer(opt.v); setSleepOpen(false); }}
                        data-testid={`player-sleep-option-${opt.v == null ? "off" : opt.v}`}
                        className="h-11 px-4 rounded-full font-ui font-bold text-sm"
                        style={{ background: active ? "var(--player-accent)" : "rgba(255,255,255,0.10)", color: active ? "var(--player-bg)" : "var(--player-text)" }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="flex items-center gap-3 w-full sm:w-72 sm:mx-auto">
                <button onClick={() => changeVolume(volume > 0 ? 0 : 1)} aria-label="Mute" className="shrink-0" style={{ color: "var(--player-text)" }}>
                  {volume > 0 ? <Volume2 size={22} /> : <VolumeX size={22} />}
                </button>
                <input type="range" className="sk-range" min={0} max={1} step={0.05} value={volume} onChange={(e) => changeVolume(parseFloat(e.target.value))} aria-label="Volume" style={{ background: `linear-gradient(to right, var(--player-accent) ${volume * 100}%, rgba(255,255,255,0.18) ${volume * 100}%)` }} />
              </div>

              <div className="flex justify-center pt-1">
                <ShareButton
                  testid="player-share"
                  dark
                  text={`🎵 "${title}" — ${current.artist}`}
                  url={`${window.location.origin}${(current.tags || []).includes("audiobook") ? "/rooms/poth-kamare" : `/rooms/sindu-kamare?song=${current.id}`}`}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
